"use client";

import { useState, useRef, useEffect } from "react";
import { X, Maximize2, Minimize2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader } from "../../components/ui/card";

export function FloatingImageViewer({ initialPosition = { x: 100, y: 100 }, initialSize = { width: 600, height: 450 }, className, rows, columns, renderNonogramGrid, generateGridStyle, onClick }) {
  const positionRef = useRef(initialPosition);
  const [, forceRender] = useState({});
  const [size, setSize] = useState(initialSize);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  // Handle dragging
  const handleMouseDown = (e) => {
    if (isFullscreen) return;

    e.preventDefault();
    setIsDragging(true);
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      // Store both client position and scroll position in dragOffset
      setDragOffset({
        x: e.clientX - rect.left + window.pageXOffset,
        y: e.clientY - rect.top + window.pageYOffset,
      });

      if (containerRef.current) {
        containerRef.current.style.transition = "none";
      }
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    e.preventDefault();

    requestAnimationFrame(() => {
      // Calculate new position using scroll-aware offset
      const newX = e.clientX + window.pageXOffset - dragOffset.x;
      const newY = e.clientY + window.pageYOffset - dragOffset.y;

      // Get window dimensions
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const scrollX = window.pageXOffset;
      const scrollY = window.pageYOffset;

      // Calculate boundaries including scroll position
      const maxX = windowWidth - size.width;
      const maxY = windowHeight - size.height;

      // Constrain position within boundaries
      positionRef.current = {
        x: Math.max(scrollX, Math.min(newX, maxX + scrollX)),
        y: Math.max(scrollY + 60, Math.min(newY, maxY + scrollY)),
      };

      // Update transform
      if (containerRef.current && !isFullscreen) {
        containerRef.current.style.transform = `translate3d(${positionRef.current.x}px, ${positionRef.current.y}px, 0)`;
      }
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);

    // Add a smooth transition when releasing
    if (containerRef.current) {
      containerRef.current.style.transition = "transform 0.1s ease-out";
    }

    // Force a re-render to update React state with the final position
    forceRender({});
  };

  // Add these functions for touch events
  const handleTouchStart = (e) => {
    if (isFullscreen) return;

    e.preventDefault();
    setIsDragging(true);
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect && e.touches[0]) {
      setDragOffset({
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      });
    }
  };
  const handleTouchMove = (e) => {
    if (!isDragging) return;

    e.preventDefault();

    if (e.touches[0]) {
      requestAnimationFrame(() => {
        const newX = e.touches[0].clientX - dragOffset.x;
        const newY = e.touches[0].clientY - dragOffset.y;

        // Get window dimensions
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        // Calculate boundaries to keep the window within the screen
        const maxX = windowWidth - size.width;
        const maxY = windowHeight - size.height;

        // Constrain position within boundaries
        positionRef.current = {
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        };

        // Update the transform directly for smoother movement
        if (containerRef.current && !isFullscreen) {
          containerRef.current.style.transform = `translate3d(${positionRef.current.x}px, ${positionRef.current.y}px, 0)`;
        }
      });
    }
  };

  // Add and remove event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleMouseUp);
    };
  }, [isDragging]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (isFullscreen) return;

      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      // Ensure the window stays within the screen after resize
      const maxX = windowWidth - size.width;
      const maxY = windowHeight - size.height;

      positionRef.current = {
        x: Math.max(0, Math.min(positionRef.current.x, maxX)),
        y: Math.max(0, Math.min(positionRef.current.y, maxY)),
      };

      if (containerRef.current) {
        containerRef.current.style.transform = `translate3d(${positionRef.current.x}px, ${positionRef.current.y}px, 0)`;
      }

      // Force a re-render
      forceRender({});
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [size, isFullscreen]);

  const handleScroll = () => {
    if (isFullscreen || !containerRef.current) return;

    requestAnimationFrame(() => {
      // Get window dimensions and scroll position
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const scrollX = window.pageXOffset;
      const scrollY = window.pageYOffset;

      // Calculate boundaries including scroll position
      const maxX = windowWidth - size.width;
      const maxY = windowHeight - size.height;

      // Adjust position based on scroll
      positionRef.current = {
        x: Math.max(scrollX, Math.min(positionRef.current.x, maxX + scrollX)),
        y: Math.max(scrollY + 60, Math.min(positionRef.current.y, maxY + scrollY)),
      };

      // Update transform
      containerRef.current.style.transform = `translate3d(${positionRef.current.x}px, ${positionRef.current.y}px, 0)`;
    });
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [size, isFullscreen]);

  // Add a class to the body when dragging to prevent text selection
  useEffect(() => {
    if (isDragging) {
      document.body.classList.add("select-none");
    } else {
      document.body.classList.remove("select-none");
    }

    return () => {
      document.body.classList.remove("select-none");
    };
  }, [isDragging]);

  // Toggle fullscreen
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <Card
      ref={containerRef}
      className={cn(
        "absolute !z-[9999] shadow-lg overflow-hidden min-h-fit transition-all duration-200 will-change-transform",
        isFullscreen ? "fixed inset-0 z-50 rounded-none" : "rounded-lg",
        className
      )}
      style={{
        ...(isFullscreen
          ? { width: "100%", height: "100%", marginTop: "60px" }
          : {
              width: `${size.width}px`,
              height: `${size.height}px`,
              position: "absolute",
              left: 0,
              top: 0,
              transform: `translate3d(${positionRef.current.x}px, ${positionRef.current.y}px, 0)`,
            }),
        pointerEvents: "all", // Ensure clicks are captured
      }}
    >
      <CardHeader
        className={cn("p-3 cursor-move flex flex-row items-center justify-between bg-primary/5 select-none", isDragging && "cursor-grabbing")}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleFullscreen}>
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClick}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0 flex-grow relative">
        <div className="w-full h-full flex items-center justify-center bg-background/50">
          <div className="clue-wrapper" style={{ display: "grid", gridTemplateColumns: "auto 1fr", gridGap: "10px" }}>
            {/* Row clues */}
            <div className="row-clues">
              {/* Placeholder to align column clues */}
              <div style={{ width: "50px", height: "101px" }} />
              {/* Render row clues */}
              {rows.map((rowClue, index) => (
                <div
                  key={`row-clue-${index}`}
                  style={{
                    textAlign: "right",
                    padding: "1.5px",
                    width: "70px",
                    height: "20px",
                    alignSelf: "center",
                    justifySelf: "center",
                    color: rowClue.length > 5 ? "red" : "black", // Highlight clue if too long
                  }}
                >
                  {rowClue.join(" ")} {/* Display clues separated by spaces */}
                </div>
              ))}
            </div>
            <div>
              {/* Column clues */}
              <div className="column-clues" style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
                {/* Render column clues */}
                {columns.map((columnClue, index) => (
                  <div
                    key={`column-clue-${index}`}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      marginRight: "3px",
                      width: "20px",
                      height: "90px",
                      color: columnClue.length > 5 ? "red" : "black", // Highlight clue if too long
                    }}
                  >
                    {columnClue.map((clue, clueIndex) => (
                      <div key={`column-clue-${index}-${clueIndex}`} style={{ marginBottom: "1px" }}>
                        {clue} {/* Display individual clue number */}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {/* Nonogram grid */}
              <div className="nonogram-grid" style={generateGridStyle()}>
                {renderNonogramGrid()} {/* Render the grid cells */}
              </div>
            </div>
            <br />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
